import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Instagram, Twitter, Youtube, Facebook, Mail, Phone, MapPin } from 'lucide-react';

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navLinks = [
    { label: 'Services', href: 'services' },
    { label: 'Elite Program', href: 'elite-program' },
    { label: 'Schedule', href: 'schedule' },
    { label: 'Videos', href: 'videos' }
  ];

  const socialLinks = [
    { icon: Instagram, url: 'https://www.instagram.com/coachmattpugh/', label: 'Instagram' },
    { icon: Twitter, url: 'https://x.com/CoachMattPugh', label: 'Twitter' },
    { icon: Youtube, url: 'https://www.youtube.com/@mattpughpuresweatbasketbal6908', label: 'YouTube' },
    { icon: Facebook, url: 'https://www.facebook.com/mattpughpuresweat/', label: 'Facebook' }
  ];

  return (
    <footer id="contact" className="py-24 bg-[#030912] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          <div>
            <div className="mb-6">
              <img
                src="/hoopiq-logo-transparent.png"
                alt="HoopIQ"
                className="w-48 h-auto object-contain mb-4"
                style={{ filter: 'invert(1) hue-rotate(180deg)' }}
                data-testid="footer-hoopiq-logo"
              />
            </div>
            <p className="text-[#94A3B8] leading-relaxed mb-6">
              Elite basketball training with professional coach Matt Pugh. Developing players from high school to the NBA.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#0A2240] border border-white/10 rounded-sm flex items-center justify-center text-white hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all"
                    aria-label={social.label}
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-6">Quick Links</h3>
            <nav className="space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="block text-[#94A3B8] hover:text-white transition-colors"
                  data-testid={`footer-nav-${link.href}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-6">Contact</h3>
            <div className="space-y-4">
              <a
                href="mailto:mpugh@puresweatbasketball.com"
                className="flex items-start gap-3 text-[#94A3B8] hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail size={18} className="mt-1 text-[#C9A84C] flex-shrink-0" />
                <span>mpugh@puresweatbasketball.com</span>
              </a>
              <a
                href="tel:+15734330599"
                className="flex items-start gap-3 text-[#94A3B8] hover:text-white transition-colors"
                data-testid="footer-phone"
              >
                <Phone size={18} className="mt-1 text-[#C9A84C] flex-shrink-0" />
                <span>(573) 433-0599</span>
              </a>
              <div className="flex items-start gap-3 text-[#94A3B8]">
                <MapPin size={18} className="mt-1 text-[#C9A84C] flex-shrink-0" />
                <span>Hannibal, MO</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#64748B] text-sm">
              © {new Date().getFullYear()} HoopIQ. All rights reserved.
            </p>
            <p className="text-[#64748B] text-sm">
              Professional Basketball Training by Matt Pugh
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};